import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, ChevronDown, ArrowUpDown } from "lucide-react";
import { analyticsAPI } from "../api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceDot, Area, ComposedChart, Bar, Legend
} from "recharts";

const RANGE_OPTIONS = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "12h", hours: 12 },
  { label: "24h", hours: 24 },
  { label: "3 ngày", hours: 72 },
  { label: "7 ngày", hours: 168 },
];

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
}

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }) +
    " " + d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-gray-800 text-white text-xs p-3 rounded-lg shadow-lg max-w-xs">
      <p className="font-bold mb-1">{formatDateTime(d.time)}</p>
      <p>Tổng hội thoại: <span className="text-purple-400 font-bold">{d.total_convs}</span></p>
      <p>Đang mở: <span className="text-yellow-400 font-bold">{d.open_count}</span></p>
      <p>Mới tạo: <span className="text-green-400">{d.new_count}</span></p>
      <p>Đã giải quyết: <span className="text-blue-400">{d.resolved_count}</span></p>
      <p>Tin nhắn: <span className="text-indigo-400">{d.total_messages}</span></p>
      {d.spike_detected && (
        <div className="mt-2 pt-2 border-t border-red-500">
          <p className="text-red-400 font-bold">⚠ SỐ HỘI THOẠI MỞ VƯỢT NGƯỠNG</p>
        </div>
      )}
    </div>
  );
};

export default function HeartbeatPage() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [spikes, setSpikes] = useState([]);
  const [range, setRange] = useState(168);
  const [realtime, setRealtime] = useState(false);
  const [loading, setLoading] = useState(true);
  const [returnVisits, setReturnVisits] = useState(null);
  const [spikeLimit, setSpikeLimit] = useState(5);
  const [sortKey, setSortKey] = useState("return_count");
  const [sortOrder, setSortOrder] = useState("desc");
  const intervalRef = useRef(null);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("desc");
    }
  };

  const sortedCustomers = React.useMemo(() => {
    if (!returnVisits?.repeat_customers) return [];
    return [...returnVisits.repeat_customers].sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];
      
      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";

      if (sortKey === "last_visit") {
         valA = new Date(valA).getTime() || 0;
         valB = new Date(valB).getTime() || 0;
      } else if (sortKey === "name" || sortKey === "platform") {
         valA = valA.toString().toLowerCase();
         valB = valB.toString().toLowerCase();
      } else if (sortKey === "warning") {
         valA = valA ? 1 : 0;
         valB = valB ? 1 : 0;
      }
      
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [returnVisits, sortKey, sortOrder]);

  const fetchData = useCallback(async () => {
    try {
      const [hb, rv] = await Promise.all([
        analyticsAPI.heartbeat({ hours: range, platform: "zalo" }),
        analyticsAPI.returnVisits({ days: 7, min_returns: 2 }),
      ]);
      setData(hb.data.data || []);
      setSpikes(hb.data.spikes || []);
      setReturnVisits(rv.data);
    } catch (err) {
      console.error("Heartbeat fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (realtime) {
      intervalRef.current = setInterval(fetchData, 30000); // 30s refresh
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [realtime, fetchData]);

  const latestOpen = data.length > 0 ? data[data.length - 1].open_count : 0;
  const totalConvs = data.length > 0 ? data[data.length - 1].total_convs : 0;
  const totalNew = data.reduce((s, d) => s + d.new_count, 0);
  const totalResolved = data.reduce((s, d) => s + d.resolved_count, 0);
  const totalMsgs = data.reduce((s, d) => s + d.total_messages, 0);
  const spikeCount = spikes.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Giám sát hoạt động</h1>
          <p className="text-sm text-gray-500 mt-1">Theo dõi thời gian thực diễn biến hội thoại — tự động cảnh báo tải đột biến</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Range selector */}
          <div className="flex flex-wrap bg-gray-100 rounded-lg p-1">
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.hours}
                onClick={() => setRange(opt.hours)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  range === opt.hours
                    ? "bg-white shadow text-primary-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Realtime toggle */}
          <button
            onClick={() => setRealtime(!realtime)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              realtime
                ? "bg-green-50 text-green-700 border border-green-300"
                : "bg-gray-100 text-gray-600 border border-gray-200"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${realtime ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
            {realtime ? "Live" : "Realtime"}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase">Tổng hội thoại</p>
          <p className="text-2xl font-bold text-purple-600">{totalConvs}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase">Đang mở</p>
          <p className="text-2xl font-bold text-yellow-600">{latestOpen}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase">Mới tạo</p>
          <p className="text-2xl font-bold text-green-600">{totalNew}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase">Đã xử lý</p>
          <p className="text-2xl font-bold text-blue-600">{totalResolved}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase">Tin nhắn</p>
          <p className="text-2xl font-bold text-indigo-600">{totalMsgs}</p>
        </div>
        <div className={`rounded-xl p-4 shadow-sm border ${spikeCount > 0 ? "bg-red-50 border-red-200" : "bg-white"}`}>
          <p className="text-xs text-gray-500 uppercase">Cảnh báo</p>
          <p className={`text-2xl font-bold ${spikeCount > 0 ? "text-red-600" : "text-gray-400"}`}>{spikeCount}</p>
        </div>
      </div>

      {/* Main Chart */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Biểu đồ nhịp hội thoại — Conversations
          {realtime && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Auto-refresh 30s</span>}
        </h2>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-400">
            Chưa có dữ liệu heartbeat. Hệ thống sẽ ghi nhận sau mỗi lần sync (5 phút).
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={data}>
              <defs>
                <linearGradient id="openGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="convGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="time"
                tickFormatter={formatTime}
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="total_convs"
                fill="url(#convGradient)"
                stroke="#1e3a8a"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Tổng HT"
                dot={false}
                isAnimationActive={false}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="open_count"
                fill="url(#openGradient)"
                stroke="#f59e0b"
                strokeWidth={2}
                name="Đang mở"
                dot={false}
                isAnimationActive={false}
              />
              <Bar yAxisId="left" dataKey="new_count" fill="#14b8a6" opacity={0.6} name="Mới" isAnimationActive={false} />
              <Bar yAxisId="left" dataKey="resolved_count" fill="#0f766e" opacity={0.6} name="Xử lý" isAnimationActive={false} />
              {/* Spike markers */}
              {data.filter(d => d.spike_detected).map((d, i) => (
                <ReferenceDot
                  key={i}
                  x={d.time}
                  y={d.open_count}
                  yAxisId="left"
                  r={6}
                  fill="red"
                  stroke="white"
                  strokeWidth={2}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Spike Log */}
      {spikes.length > 0 && (
        <div className="bg-red-50 rounded-xl border border-red-200 p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-3">Nhật ký cảnh báo ({spikes.length})</h2>
          <div className="space-y-2">
            {spikes.slice(0, spikeLimit).map((s, i) => {
              const cleanAnalysis = s.analysis && s.analysis.startsWith("Phát hiện spike") ? s.analysis : null;
              // Trích số conv trong giờ từ analysis: "Phát hiện spike: 14 hội thoại..."
              const convMatch = s.analysis && s.analysis.match(/Phát hiện spike:\s*(\d+)\s*hội thoại/);
              const convThisHour = convMatch ? convMatch[1] : null;
              return (
              <div key={i} className="bg-white rounded-lg px-3.5 sm:px-4 py-3 border border-red-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <span className="text-sm font-semibold text-red-700 whitespace-nowrap">{formatDateTime(s.time)}</span>
                  <span className="text-sm text-gray-600 truncate">
                    {cleanAnalysis
                      ? cleanAnalysis.split(".")[0] + "."
                      : `Số hội thoại mới trong giờ vượt ngưỡng bất thường`
                    }
                  </span>
                  {s.topics && s.topics.length > 0 && (
                    <div className="hidden sm:flex gap-1 flex-shrink-0">
                      {s.topics.slice(0, 3).map((t, j) => (
                        <span key={j} className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-200">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded whitespace-nowrap flex-shrink-0">
                  {convThisHour ? `${convThisHour} conv/giờ` : `${s.open_count} đang mở`}
                </span>
              </div>
            );
            })}
          </div>
          {spikes.length > spikeLimit && (
            <button
              onClick={() => setSpikeLimit(prev => prev + 10)}
              className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-sm text-red-600 hover:bg-red-100 rounded-lg font-medium transition-colors"
            >
              ▼ Xem thêm ({spikes.length - spikeLimit} còn lại)
            </button>
          )}
        </div>
      )}

      {/* Return Visits */}
      {returnVisits && returnVisits.repeat_customers && returnVisits.repeat_customers.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Khách hàng quay lại</h2>
          <p className="text-sm text-gray-500 mb-4">
            Quay lại ≥ 2 lần trong <strong>7 ngày gần nhất</strong> — danh sách tự làm sạch khi không còn quay lại
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[650px]">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  {[
                    { key: "name", label: "Khách hàng", align: "left" },
                    { key: "platform", label: "Nguồn", align: "left" },
                    { key: "return_count", label: "Lần / 7 ngày", align: "center" },
                    { key: "total_return_count", label: "Tổng lịch sử", align: "center" },
                    { key: "avg_gap_hours", label: "TB khoảng cách", align: "center" },
                    { key: "last_visit", label: "Lần cuối", align: "center" },
                    { key: "warning", label: "Cảnh báo", align: "center" },
                  ].map(({ key, label, align }) => (
                    <th 
                      key={key}
                      onClick={() => handleSort(key)}
                      className={`pb-2 font-medium cursor-pointer hover:text-blue-700 transition-colors select-none ${align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left"} group`}
                    >
                      <div className={`flex items-center gap-1 ${align === "center" ? "justify-center" : align === "right" ? "justify-end" : "justify-start"}`}>
                        {label}
                        {sortKey === key ? (
                          sortOrder === "asc" ? <ChevronUp className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedCustomers.map(c => (
                  <tr key={c.id} className={`border-b ${c.warning ? "bg-amber-50" : ""}`}>
                    <td className="py-2 font-medium">
                      <button
                        onClick={() => navigate(`/conversations?customer_id=${c.id}&customer_name=${encodeURIComponent(c.name)}`)}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                      >
                        {c.name}
                      </button>
                    </td>
                    <td className="py-2 capitalize">{c.platform}</td>
                    <td className="py-2 text-center font-bold text-amber-600">{c.return_count}</td>
                    <td className="py-2 text-center text-gray-400">{c.total_return_count}</td>
                    <td className="py-2 text-center">{c.avg_gap_hours}h</td>
                    <td className="py-2 text-center text-xs text-gray-500">
                      {c.last_visit ? new Date(c.last_visit).toLocaleDateString("vi-VN") : "—"}
                    </td>
                    <td className="py-2 text-center">
                      {c.warning && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                          ⚠ Chưa xử lý dứt điểm
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
